/*
Control de ventas: Muestralas ventas aprobadas que no hayan terminado sus
actividades post venta.
Autor: Benjamín León
Fecha: 2025-03-09
*/
import React, { useEffect, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'

import { useAuth } from '../hooks/useAuth'
import useSalenotesFetcher from '../hooks/useSalenotesFetcher'
import { Salenote, SalenoteTasks } from '../interfaces/Salenote'
import { fechaIsoToDD_MM_YYYY } from '../utils/utils'
import useSalenotesTasksPoster from '../hooks/useSalenotesTasksPoster'
import filterContains from '../utils/filterContains'

import DisplayError from './DisplayError'
import ConfirmationModal from './ConfirmationModal'
import { Info } from './MessageInfo'
import BranchUserFilter, { BranchUserFilterForm } from './BranchUserFilter'
import { ButtonAction, ButtonActionShort } from './Buttons'

interface SalenoteMod extends SalenoteTasks {
  modSw?: boolean
  user_name: string
}
const filterDataIni = {
  branch_name: '',
  user_name: '',
}
const SalesControl: React.FC = () => {
  const [data, setData] = useState<SalenoteMod[]>([])
  const [array, setArray] = useState<SalenoteMod[]>([])
  const { userData } = useAuth()
  const { fetchSalenotesTasks } = useSalenotesFetcher()
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
  const { salenotesTasksEdit, errorSalenotesTasks } = useSalenotesTasksPoster()
  const { salenoteTasksConfirm, errorSalenoteTasksConfirm } = useSalenotesTasksPoster()
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<SalenoteMod>()
  const [infoMessage, setInfoMessage] = useState('')
  const [filterData, setFilterData] = useState<BranchUserFilterForm>(filterDataIni)
  const { setIsDirty } = useAuth()

  useEffect(() => {
    // Buscamos las salenotes que estan por confirmar
    fetchAsync(userData?.organization?._id ?? '')
  }, [])

  const fetchAsync = async (orgId: string) => {
    const salenotes = await fetchSalenotesTasks(orgId)

    if (salenotes.length === 0) return

    // Una nota de venta cargada a medias (sin body.profile) hacía fallar el map
    // completo y la pantalla quedaba vacía, escondiendo todas las demás.
    const salenotesAux = salenotes.map((s) => {
      const user = s.body?.profile?.user

      return { ...s, user_name: user ? `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() : '' }
    })
    setData(salenotesAux)
    //Al principio, el array es igual a lo que llega
    setArray(salenotesAux)
  }
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!buttonDisabled) {
        event.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [buttonDisabled])

  const dateSort = (rowA: SalenoteTasks, rowB: SalenoteTasks) => {
    const a = rowA.approvedAt!
    const b = rowB.approvedAt!

    if (a > b) {
      return 1
    }

    if (b > a) {
      return -1
    }

    return 0
  }

  const handleCheckboxChange = (noteNumber: string, field: keyof NonNullable<Salenote['tasks']>) => {
    setButtonDisabled(false)
    setIsDirty(true)
    setData((prevData) => {
      if (!prevData) return []

      return prevData.map((note) =>
        note._id === noteNumber
          ? {
              ...note,
              modSw: true,
              tasks: {
                transfer: note.tasks?.transfer ?? false,
                tag: note.tasks?.tag ?? false,
                credit: note.tasks?.credit ?? false,
                creditFee: note.tasks?.creditFee ?? false,

                [field]: !note.tasks?.[field],
              },
            }
          : note
      )
    })
    // Por ahora, duplicamos

    setArray((prevData) => {
      if (!prevData) return []

      return prevData.map((note) =>
        note._id === noteNumber
          ? {
              ...note,
              modSw: true,
              tasks: {
                transfer: note.tasks?.transfer ?? false,
                tag: note.tasks?.tag ?? false,
                credit: note.tasks?.credit ?? false,
                creditFee: note.tasks?.creditFee ?? false,

                [field]: !note.tasks?.[field],
              },
            }
          : note
      )
    })
  }

  const columns: TableColumn<SalenoteMod>[] = [
    { name: 'Número', selector: (row) => row.number, sortable: true, grow: 1 },
    {
      name: 'Vehículo',
      // Mismo motivo que en fetchAsync: un registro incompleto no debe romper la grilla
      selector: (row) =>
        row.body?.vehicle ? `${row.body.vehicle.brand} ${row.body.vehicle.model} [${row.body.vehicle.year}]` : '',
      sortable: true,
      grow: 3,
      wrap: true,
    },
    { name: 'Patente', selector: (row) => row.body?.vehicle?.plate ?? '', sortable: true },
    {
      name: 'Sucursal',
      selector: (row) => row.branch_name ?? '',
      sortable: true,
    },
    {
      name: 'Vendedor',
      selector: (row) => row.user_name,
      sortable: true,
      grow: 2,
    },
    {
      name: <div>Fecha aprobación</div>,
      selector: (row) => fechaIsoToDD_MM_YYYY(row.approvedAt),
      sortable: true,
      sortFunction: dateSort,
    },

    {
      name: 'Transferencia',
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.tasks?.transfer ?? false}
          onChange={() => handleCheckboxChange(row._id, 'transfer')}
        />
      ),
      sortable: true,
      sortFunction: (a, b) => Number(b.tasks?.transfer ?? false) - Number(a.tasks?.transfer ?? false),
    },
    {
      name: 'TAG',
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.tasks?.tag ?? false}
          onChange={() => handleCheckboxChange(row._id, 'tag')}
        />
      ),
      sortable: true,
      sortFunction: (a, b) => Number(b.tasks?.tag ?? false) - Number(a.tasks?.tag ?? false),
    },
    {
      name: <div>Saldo Crédito</div>,
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.tasks?.credit ?? false}
          onChange={() => handleCheckboxChange(row._id, 'credit')}
        />
      ),
      sortable: true,
      sortFunction: (a, b) => Number(b.tasks?.credit ?? false) - Number(a.tasks?.credit ?? false),
    },
    {
      name: <div>Factura comisión de Crédito</div>,
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.tasks?.creditFee ?? false}
          onChange={() => handleCheckboxChange(row._id, 'creditFee')}
        />
      ),
      sortable: true,
      sortFunction: (a, b) => Number(b.tasks?.creditFee ?? false) - Number(a.tasks?.creditFee ?? false),
    },
    {
      name: 'Acciones',
      cell: (row) => (
        <ButtonActionShort
          label="Confirmar"
          disabled={!row.tasks?.credit || !row.tasks?.creditFee || !row.tasks?.tag || !row.tasks?.transfer}
          handleButtonClick={(e) => handleConfirmClick(e, row)}
        />
      ),
      grow: 2,
    },
  ]

  const handleConfirmClick = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: SalenoteMod) => {
    if (!row.tasks?.credit || !row.tasks?.creditFee || !row.tasks?.tag || !row.tasks?.transfer) return
    setSelectedRow(row)
    setConfirmationModalOpen(true)
  }

  const handleGuardarClick = async () => {
    // Solo los que tienen modSw = true
    if (buttonDisabled) return
    const modified = data.filter((d) => d.modSw)

    if (modified.length > 0) {
      const resp = await salenotesTasksEdit(modified)

      if (resp) {
        setInfoMessage('Datos guardados.')
        setButtonDisabled(true)
        setIsDirty(false)
      }
    }
  }

  // Propiedad de BranchUserFilter
  const updFilterData = (filterData: BranchUserFilterForm) => {
    setFilterData(filterData)

    // Ahora, filtramos según el contenido de filterData

    let filtered = filterContains({
      list: data,
      filterField: (item) => item.branch_name,
      searchTerm: filterData.branch_name,
    })

    filtered = filterContains({
      list: filtered,
      filterField: (item) => item.user_name,
      searchTerm: filterData.user_name,
    })
    setArray(filtered)
  }

  // Guardamos y confirmamos los tasks de la fila
  const handleConfirmTasks = async () => {
    setConfirmationModalOpen(false)

    // Lo siguiente no debería darse
    if (!selectedRow) return

    const resp = await salenoteTasksConfirm(selectedRow, userData?.profile?._id ?? '')

    if (resp) {
      // nos fue bien. Borramos la fila
      const existing = data.filter((d) => d._id !== selectedRow._id)
      setData(existing)
      // idem con array
      const existingArray = array.filter((d) => d._id !== selectedRow._id)
      setArray(existingArray)

      // Y veamos si tenemos que deshabilitar el boton Guardar
      const modified = existing.find((d) => d.modSw)

      if (!modified) {
        setButtonDisabled(true)
        setIsDirty(false)
      }
    }
  }
  const onInfoTimeout = (): void => {
    setInfoMessage('')
  }
  const handleConfirmCancel = () => {
    setConfirmationModalOpen(false)
  }

  return (
    <div>
      <div className="mt-4 mb-4">
        <BranchUserFilter formData={filterData} updFormData={updFilterData} />
      </div>
      <div className="p-4 flex flex-col items-center">
        <div className="w-full overflow-x-auto">
          <DataTable columns={columns} data={array} pagination dense />
        </div>

        <ButtonAction label="Guardar" disabled={buttonDisabled} handleButtonClick={handleGuardarClick} />
        <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} noCenter={true} />

        <DisplayError msg={errorSalenotesTasks?.message ?? ''} />
        <DisplayError msg={errorSalenoteTasksConfirm?.message ?? ''} />
        {confirmationModalOpen && (
          <ConfirmationModal
            message={`¿Estás seguro de que todas las actividades de la Nota de venta ${selectedRow!.number} han sido efectuadas? Esta acción no se puede revertir.`}
            onConfirm={handleConfirmTasks}
            onCancel={handleConfirmCancel}
          />
        )}
      </div>
    </div>
  )
}

export default SalesControl
